/// <reference lib="dom" />
/**
 * webTileDraw.web.ts — Canvas2D draw helpers: parallax, tiles, greybox.
 * Web-only (.web.ts). Stateless reads — never mutates gameState.
 *
 * Portal/pedestal helpers live in webPortalDraw.web.ts (re-exported below
 * for backward compatibility with existing import sites).
 *
 * @module webTileDraw
 */

import {
  VIEWPORT_WIDTH,
  VIEWPORT_HEIGHT,
  NOVA_WIDTH,
  NOVA_HEIGHT,
  TILE_SIZE,
} from '../../constants/gameConstants';
import {
  PLATFORM_COLOR,
  PLATFORM_EDGE_COLOR,
  NOVA_COLORS,
} from '../../constants/rendering';
import { gameState } from '../engine/gameState';
import { tileSourceRect, visibleTileRange, TILESET_TILE_PX } from './TileRenderer';
import type { TiledMap, TiledTileLayer } from './TileMapTypes';
import { drawExitPortal } from './webPortalDraw';

/**
 * Draw a parallax background layer, tiling horizontally.
 *
 * When `mirror` is true the second copy is drawn horizontally flipped so the
 * right edge of the normal image meets its own mirror — seamless even if the
 * source image's left/right edges don't match. Use this for backgrounds that
 * were not authored as seamlessly tileable.
 */
export function drawParallax(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  camX: number,
  ratio: number,
  mirror = false,
): void {
  if (!img.complete || img.naturalWidth === 0) return;

  const W = img.naturalWidth;

  if (!mirror) {
    // DECISION: Round first, then normalise into [0, W). `%` in JS keeps the
    // sign of the dividend, so a negative camX produced a negative scrollX,
    // which drew the image shifted *right* and then skipped the second copy
    // (`scrollX > 0` was false) — leaving a bare column at the left edge.
    // camX goes negative whenever the camera is clamped to x = 0 and screen
    // shake subtracts a positive offset (see gameRenderFrame: `camX -= shakeX`).
    // The mirror branch below already normalised; this branch did not.
    // Rounding before the wrap also avoids scrollX landing exactly on W.
    const scrollX = ((Math.round(camX * ratio) % W) + W) % W;
    // `|| 0` normalises -0, which is harmless to Canvas but surprises any
    // caller or test doing an exact comparison on the offset.
    const offsetX = -scrollX || 0;
    // NOTE: the second copy is placed at W, so this is only seamless while
    // every parallax image is exactly VIEWPORT_WIDTH wide (all of them are —
    // 480x270; the 256px World 2 backgrounds go through the space renderer).
    ctx.drawImage(img, offsetX, 0, VIEWPORT_WIDTH, VIEWPORT_HEIGHT);
    if (scrollX > 0) {
      ctx.drawImage(img, W - scrollX, 0, VIEWPORT_WIDTH, VIEWPORT_HEIGHT);
    }
    return;
  }

  // DECISION: Mirror-tile for non-seamless backgrounds — period 2×W.
  const period = 2 * W;
  const rawScroll = camX * ratio;
  const scroll = Math.round(((rawScroll % period) + period) % period);

  let x = -scroll;
  let tileIdx = 0;

  // Skip fully off-screen tiles on the left
  while (x + W <= 0) { x += W; tileIdx++; }

  // Draw tiles until the viewport is covered
  while (x < VIEWPORT_WIDTH) {
    const rx = Math.round(x);
    if (tileIdx % 2 === 1) {
      ctx.save();
      ctx.translate(rx + W, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(img, 0, 0, W, VIEWPORT_HEIGHT);
      ctx.restore();
    } else {
      ctx.drawImage(img, rx, 0, W, VIEWPORT_HEIGHT);
    }
    x += W;
    tileIdx++;
  }
}

/**
 * Draw the specified tile layers from the Tiled map, culled to the camera viewport.
 * @param skipTileIds - Optional set of tile IDs to skip (used for collectibles
 *   drawn separately from gameState with collection tracking).
 */
export function drawTileLayers(
  ctx: CanvasRenderingContext2D,
  tilesetImg: HTMLImageElement,
  tiledMap: TiledMap,
  camX: number,
  camY: number,
  layerNames: readonly string[],
  skipTileIds?: ReadonlySet<number>,
): void {
  if (!tilesetImg.complete || tilesetImg.naturalWidth === 0) return;
  const { colStart, colEnd, rowStart, rowEnd } = visibleTileRange(
    camX, camY, VIEWPORT_WIDTH, VIEWPORT_HEIGHT,
    tiledMap.width, tiledMap.height,
  );
  for (const layerName of layerNames) {
    const layer = tiledMap.layers.find(
      (l): l is TiledTileLayer =>
        l.type === 'tilelayer' && l.name === layerName
    );
    if (!layer) continue;
    for (let row = rowStart; row <= rowEnd; row++) {
      for (let col = colStart; col <= colEnd; col++) {
        const tileId = layer.data[row * layer.width + col];
        if (!tileId) continue;
        if (skipTileIds?.has(tileId)) continue;
        const src = tileSourceRect(tileId);
        if (!src) continue;
        // DECISION: Floor destination coords so adjacent tiles always abut
        // without gaps. Math.round can round neighbours in opposite
        // directions, producing 1 px hairline tears in the dirt.  Floor is
        // monotonic — col+1 always gets floor(col+1)*TILE_SIZE which is
        // exactly floor(col)*TILE_SIZE + TILE_SIZE (integer TILE_SIZE).
        // The +1 dest-size overlap is a safety net for any remaining
        // fractional camera offset and is invisible under nearest-neighbor.
        const dx = Math.floor(col * TILE_SIZE - camX);
        const dy = Math.floor(row * TILE_SIZE - camY);
        ctx.drawImage(
          tilesetImg,
          src.sx, src.sy, TILESET_TILE_PX, TILESET_TILE_PX,
          dx, dy, TILE_SIZE + 1, TILE_SIZE + 1,
        );
      }
    }
  }
}

// ── Ground fill + pit gradients extracted to webGroundDraw.web.ts ─────────

// Re-export for backward compatibility with existing imports.
export { drawGroundFill, drawPitGradients } from './webGroundDraw';

// ── Portal helpers extracted to webPortalDraw.web.ts ──────────────────────
// Re-export for backward compatibility with existing imports.
export { drawExitPortal, drawExitPortalFront } from './webPortalDraw';

/**
 * Greybox draw path — used when sprite assets are not yet loaded.
 * Renders the same scene as the sprite path using coloured rectangles, so the
 * game is playable before assets finish loading or if asset loading fails.
 */
export function drawGreybox(
  ctx: CanvasRenderingContext2D,
  camX: number,
  camY: number,
): void {
  const { level, nova } = gameState;
  for (const p of level.platforms) {
    ctx.fillStyle = PLATFORM_COLOR;
    ctx.fillRect(p.x - camX, p.y - camY, p.width, p.height);
    ctx.fillStyle = PLATFORM_EDGE_COLOR;
    ctx.fillRect(p.x - camX, p.y - camY, p.width, 2);
  }

  drawExitPortal(ctx, camX, camY, null, null, null, false);

  // DECISION: Guard with hurtFlash so stomp-bounce invincibility does not blink.
  const hurtBlink = nova.hurtFlash && nova.invincibilityFrames > 0 && Math.floor(nova.invincibilityFrames / 4) % 2 === 0;
  if (!hurtBlink) {
    ctx.fillStyle = (NOVA_COLORS as Record<string, string>)[nova.state] ?? NOVA_COLORS['IDLE'];
    ctx.fillRect(nova.x - camX, nova.y - camY, NOVA_WIDTH, NOVA_HEIGHT);
  }

  ctx.fillStyle = '#f5c842';
  for (const shard of gameState.projectiles) {
    ctx.fillRect(shard.x - camX, shard.y - camY, shard.width, shard.height);
  }

  for (const enemy of gameState.enemies) {
    ctx.fillStyle = enemy.color;
    ctx.fillRect(enemy.x - camX, enemy.y - camY, enemy.width, enemy.height);
  }
}
